import { useEffect, useState } from "react";
import { MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { TextField } from "../../components/fields/Fields";
import FormController from "../../components/fields/FormController";
import { SelectFields } from "../../components/select/SelectFields";
import { useForm } from "react-hook-form";
import Button from "../../components/button/Button";
import { GroupSelectModal } from "../userList/GroupSelectModal";
import { CheckBoxGroup, CheckBoxItem } from "../../components/checkBox/CheckBoxGroup";
import Badge from "../../components/badges/Badges";
import { CheckBox } from "../../components/checkBox/CheckBox";
import { PointHistoryData, SearchPoint, PointListSearchDefault } from "../../model/PointData";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import pointDataQuery from "../../firestore/PointData.query";
import { Company } from "../../model/Companies";
import { Groups } from "../../model/Groups";

type Props = {
  pointHistoryList: PointHistoryData[];
  setSearchPointHistoryList: Function;
  setSearchPointHistoryFlg: Function;
  groupList: Groups[];
  ptNameList: any[];
  company: Company | undefined;
  onSearch: (searchCondition: any) => void;
  searchCondition: any;
};

export const PointHistorySearch = ({
  groupList,
  ptNameList,
  company,
  onSearch,
}:Props) => {
  //ポイントカテゴリ未選択時のid
  const unselected = -1;

  // アイコン設定
  const searchIcon = {icon: MagnifyingGlassIcon};
  const upIcon = {icon: ChevronUpIcon};
  const downIcon = {icon: ChevronDownIcon};

  const dispatch = useDispatch();
  const [searchItem, setSearchItem] = useState<SearchPoint>(PointListSearchDefault);
  const [searchFlg, setSearchFlg] = useState(true);
  const [selectTranType, setSelectTranType] = useState<CheckBoxItem[]>([]);
  const [checkedDate, setCheckedDate] = useState<string>("");
  const [ptNamesList, setPtNamesList] = useState([]);
  const [ptCategoryList, setCategoriesList] = useState([]);
  const [ptTypesList, setTypesList] = useState([]);
  const [isOpenGroupSelect, setIsOpenGroupSelect] = useState(false);
  const [isVoid, setIsVoid] = useState(false);
  const [userIdError, setUserIdError] = useState(false);
  const errorMessage = ConstMessage.ERR_USERID_LENGTH;
  const [companyGroupsValue, setCompanyGroupsValue] = useState<CheckBoxItem[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<CheckBoxItem[]>([]);
  const [groupSelectError, setGroupSelectError] = useState("");

  const { control } = useForm<PointHistoryData & {}>({});

  // 検索画面の表示・非表示の切り替え
  const handleClick = (e:any) => {
    e.preventDefault();
    setSearchFlg(searchFlg? false : true);
  };

  //取引種別チェックボックス
  const transactionTypeList = [
  { id: "grant", title: "付与", value: false, },
  { id: "use", title: "利用", value: false, },
  { id: "adjustment", title: "調整", value: false, },
  { id: "revocation", title: "失効", value: false, },
  ]

  //初期表示
  useEffect(() => {
    const f = async () => {
      try {
        //取引種別
        let selectTranType = transactionTypeList ? await Promise.all(transactionTypeList.map((item): CheckBoxItem => {
          return {
            id: item.id,
            title: item.title,
            value: false,
          }
        })) : [];
        setSelectTranType(selectTranType);

        //ポイントカテゴリ
        let ptCategoriesList: any = [
          { id: unselected, name: "" },
        ];
        const ptCategoryList = await pointDataQuery.getPointCategoryList();
        ptCategoryList.map((ptCategory: any) => {
          if (ptCategory.categoryId != undefined) {
            ptCategoriesList.push({ id: ptCategory.categoryId, name: ptCategory.categoryName, sortKey: ptCategory.sortKey });
          }
        })
        setCategoriesList(ptCategoriesList);

        //ポイントタイプ
        let ptTypesList: any = [
          { name: "" },
        ];
        const ptTypeList = await pointDataQuery.getPointTypeList();
        ptTypeList.map((ptType: any) => {
          if (ptType.categoryId != undefined) {
            ptTypesList.push({ id: ptType.typeId, name: ptType.typeName, sortKey: ptType.sortKey });
          }
        })
        setTypesList(ptTypesList);
      } catch (err: any) {
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      }
    }
    f();
  }, []);

  // グループセレクト画面の初期表示用
  useEffect(() => {
    const f = async () => {
        const defaultGroupsValue = groupList? await Promise.all(groupList.map((item): CheckBoxItem => {
          return {
            id: item.id,
            title: item.name,
            value: false,
            disabled: item.deleteFlg,
          }
        })): [];
        setCompanyGroupsValue(defaultGroupsValue);
        setSelectedGroups(defaultGroupsValue);
    };
    f();
  },[groupList]);

  //ポイント名
  useEffect(() => {
    let ptNamesList: any = [
      { id: "", name: "" },
    ];
    ptNameList.forEach((ptName: string) => {
      if (ptName && ptName !== "undefined") {
        ptNamesList.push({ id: ptName, name: ptName });
      }
    });
    setPtNamesList(ptNamesList);
  }, [ptNameList]);

  //チェックボックス（日付フォーム）の値を管理
  const changedDateCheckBox = (e: any, value: string) => {
    setSearchItem(
      {
        ...searchItem,
        expirationDateStart: "", expirationDateEnd: "", occurrenceDateStart: "", occurrenceDateEnd: "",
      }
    );
    setCheckedDate(value);
  }

  // グループセレクト画面の表示用
  useEffect(() => {
    const f = async () => {
      setIsVoid(!isVoid);
      if(companyGroupsValue.length > 0){
        let topic: any = [];
        await Promise.all(companyGroupsValue.map((item: any) => {
          if(item.value){
            const value = item.value;
            topic.push({id:item.id, title: item.title});
          }
        }));
        setSearchItem({...searchItem, topic:topic});
      }
    };
    f();
  },[isOpenGroupSelect]);

  const toggle = () => {
    if (companyGroupsValue.filter((group) => group.value === true).length > 30) {
      setGroupSelectError(ConstMessage.ERR_GROUP_SELECT_31_OR_MORE);
    } else {
      setGroupSelectError('');
      setSelectedGroups(companyGroupsValue);
      setIsOpenGroupSelect(!isOpenGroupSelect);
      setIsVoid(!isVoid);
    }
  };
  
  const onSelectCancel = () => {
    // 変更前のグループ選択状態をセット
    setCompanyGroupsValue(selectedGroups);
    setSearchItem({...searchItem, topic: []});
    setIsOpenGroupSelect(!isOpenGroupSelect);
    setIsVoid(!isVoid);
  }

  // 検索を実行
  const onClickSearch = async () => {
    if (searchItem.displayUserId && searchItem.displayUserId.trim() !== "") {
      const regexp = /^[0-9]{11}$/;
      if (!regexp.test(searchItem.displayUserId)) {
        setUserIdError(true);
        return;
      } else {
        setUserIdError(false);
      }
    } else {
      setUserIdError(false);
    }
    const searchCondition = {
      tenantId: company?.id!,
      userId: searchItem.displayUserId,
      name: searchItem.userName,
      ptName: searchItem.ptName,
      tranType: selectTranType.filter(t => t.value).map(t => t.title),
      ptCategoryId: searchItem.ptCategoryId,
      ptTypeId: searchItem.ptTypeId,
      expirationDateStart: searchItem.expirationDateStart,
      expirationDateEnd: searchItem.expirationDateEnd,
      occurrenceDateStart: searchItem.occurrenceDateStart,
      occurrenceDateEnd: searchItem.occurrenceDateEnd,
      groups: Array.isArray(searchItem.topic)
        ? searchItem.topic.map((g: any) => g.id)
        : [],
    };

    onSearch(searchCondition);
  };
  
  return(
    <div className="flex flex-col">
      <div className="inline-block min-w-full p-1 align-middle">
        <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full table-fixed divide-y">
            <div className="items-center p-2">
              <div className="flex justify-between cursor-pointer" onClick={(e) => handleClick(e)}>
                <h2 className=" flex font-semibold text-gray-900 items-center">
                  <searchIcon.icon
                    className={"flex-shrink-0 h-5 w-5"}
                    aria-hidden="true"
                  />
                  検索
                </h2>
                {searchFlg?(
                  <>
                    <upIcon.icon
                    className={"flex-shrink-0 h-5 w-5"}
                    aria-hidden="true"
                    />
                  </>
                ):(
                  <>
                    <downIcon.icon
                    className={"flex-shrink-0 h-5 w-5"}
                    aria-hidden="true"
                    />
                  </>
                )}
              </div>
              {searchFlg?(
                <>
                 <h3 className="text-sm text-gray-500 my-1 mt-4">&nbsp;複数項目を入力した場合はAND検索となります</h3>
                  <div className="flex">
                    <div className="w-1/3 ml-3" >
                      <div className="flex items-center justify-start flex-1 mr-2 mb-1">
                        <h3 className="text-sm text-gray-900">利用者番号　　</h3>
                        <div>
                          <TextField
                            id="displayUserId"
                            className={'h-full w-60'}
                            inputClassName={'h-8'}
                            value={searchItem.displayUserId}
                            error={userIdError}
                            helperText={errorMessage}
                            onChange={(e: any) => setSearchItem({ ...searchItem, displayUserId: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-start flex-1 mr-2 mb-1">
                        <h3 className="text-sm text-gray-900">名前　　　　　</h3>
                        <div>
                          <TextField
                            id="userName"
                            className={'h-10 w-60'}
                            inputClassName={'h-8'}
                            value={searchItem.userName}
                            onChange={(e: any) => setSearchItem({ ...searchItem, userName: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-start flex-1 flex-wrap mt-2 mb-1">
                        <h3 className="text-sm text-gray-900 mr-2">取引種別　　</h3>
                        <div>
                          <CheckBoxGroup
                            children={undefined}
                            name={"tranType"}
                            checkBoxItem={selectTranType}
                            setState={setSelectTranType}
                            vertical={true}
                            boxClassName={"mr-1"}
                            titleClassName={"mr-2 text-gray-900"}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="w-1/3 ml-3" >
                      <div className="flex items-center justify-start flex-1 mr-2 mb-1">
                        <h3 className="text-sm text-gray-900 mr-2 w-[70px]">カテゴリ　</h3>
                        <div>
                          <SelectFields
                            id="pointCategory"
                            color="indigo"
                            value={searchItem.ptCategoryId}
                            className={"h-10 w-60"}
                            selectItems={ptCategoryList}
                            onChange={(e: any) => setSearchItem({ ...searchItem, ptCategoryId: Number(e.target.value) })}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-start flex-1 mr-2 mb-1">
                        <h3 className="text-sm text-gray-900 mr-2">タイプ　　</h3>
                        <div>
                          <SelectFields
                            id="pointType"
                            color="indigo"
                            value={searchItem.ptTypeId}
                            className={"h-10 w-60"}
                            selectItems={ptTypesList}
                            onChange={(e: any) => setSearchItem({ ...searchItem, ptTypeId: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-start flex-1 mr-2 mb-1">
                        <h3 className="text-sm text-gray-900 mr-2">ポイント名</h3>
                        <div>
                          <SelectFields
                            id="ptName"
                            color="indigo"
                            value={searchItem.ptName}
                            className={"h-10 w-60"}
                            selectItems={ptNamesList}
                            onChange={(e: any) => setSearchItem({ ...searchItem, ptName: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mr-10" >
                      <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-lg h-fit">
                        <div className="p-2 flex flex-col gap-1">
                          <h3 className="text-sm text-gray-500 mb-3 ml-2">いずれか１つの項目を選択してください</h3>
                          <div className="flex items-center ml-3 flex-wrap">
                            <h3 className="text-sm text-gray-900">有効期限　　　</h3>&nbsp;
                            <div className="flex items-center">
                              <CheckBox
                                id="expirationDate"
                                value={checkedDate == "expirationDate" ? true : false}
                                onChange={(e: any) => changedDateCheckBox(e, checkedDate == "expirationDate" ? "" : "expirationDate")}
                              />
                              <FormController
                                control={control}
                                value={searchItem.expirationDateStart}
                                className={"mr-1 h-10"}
                                inputClassName={`${checkedDate == "expirationDate" ? "" : "bg-gray-300"} h-8`}
                                name={"expirationDate_s"}
                                label={""}
                                onChange={(e: any) => setSearchItem({ ...searchItem, expirationDateStart: e.target.value })}
                                type={"date"}
                                disabled={checkedDate == "expirationDate" ? false : true}
                              />
                              ～
                              <FormController
                                control={control}
                                value={searchItem.expirationDateEnd}
                                className={"ml-1 h-10"}
                                inputClassName={`${checkedDate == "expirationDate" ? "" : "bg-gray-300"} h-8`}
                                name={"expirationDate_e"}
                                label={""}
                                onChange={(e: any) => setSearchItem({ ...searchItem, expirationDateEnd: e.target.value })}
                                type={"date"}
                                disabled={checkedDate == "expirationDate" ? false : true}
                              />
                            </div>
                          </div>
                          <div className="ml-3 flex items-center justify-start flex-wrap">
                            <h3 className="text-sm text-gray-900">ポイント発生日</h3>&nbsp;
                            <div className="flex items-center">
                              <CheckBox
                                id="occurrenceDate"
                                value={checkedDate == "occurrenceDate" ? true : false}
                                onChange={(e: any) => changedDateCheckBox(e, checkedDate == "occurrenceDate" ? "" : "occurrenceDate")}
                              />
                              <FormController
                                control={control}
                                value={searchItem.occurrenceDateStart}
                                className={"mr-1 h-10"}
                                inputClassName={`${checkedDate == "occurrenceDate" ? "" : "bg-gray-300"} h-8`}
                                name={"occurrenceDate_s"}
                                label={""}
                                onChange={(e: any) => setSearchItem({ ...searchItem, occurrenceDateStart: e.target.value })}
                                type={"date"}
                                disabled={checkedDate == "occurrenceDate" ? false : true}
                              />
                              ～
                              <FormController
                                control={control}
                                value={searchItem.occurrenceDateEnd}
                                className={"ml-1 h-10"}
                                inputClassName={`${checkedDate == "occurrenceDate" ? "" : "bg-gray-300"} h-8`}
                                name={"occurrenceDate_e"}
                                label={""}
                                onChange={(e: any) => setSearchItem({ ...searchItem, occurrenceDateEnd: e.target.value })}
                                type={"date"}
                                disabled={checkedDate == "occurrenceDate" ? false : true}
                              />
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                  <div className="flex">
                  <tr className="min-w-full">
                    <div className="flex justify-between">
                        <div className="w-3/5">
                            <div className="flex min-w-full">
                              <div className="pl-3 mt-4 w-auto">
                                <div className="mt-3 flex items-center justify-start flex-1 flex-wrap">
                                  <h3 className="text-sm text-gray-900 mr-2">所属グループ </h3>
                                </div>
                              </div>
                              <div className="ml-1 mt-4 w-3/5">
                                <div className={`text-left items-start`}>
                                  <div className="h-12 w-full border-2 border-solid overflow-y-scroll">
                                    {searchItem.topic.map(
                                      (item: any, index) => (
                                        <Badge
                                          color="blue"
                                        >
                                          {item.title}
                                        </Badge>
                                      ))
                                    }
                                  </div>
                                </div>
                                <h3 className="text-xs text-gray-500">複数選択した場合いずれかに所属する利用者すべてが検索されます</h3>
                                <GroupSelectModal
                                  open={isOpenGroupSelect}
                                  toggle={toggle}
                                  closeOnClickOutside
                                  companyGroupsValue={companyGroupsValue}
                                  setState={setCompanyGroupsValue}
                                  children={undefined}
                                  dispButtonFlg={true}
                                  onSelectCancel={onSelectCancel}
                                  errorMessage={groupSelectError}
                                />
                              </div>
                              <div className="ml-1 mt-4 w-auto">
                                <div className="flex justify-start">
                                  <Button
                                    className={"h-12 w-24 mr-2 pl-4 pr-4 text-sm items-center rounded-full"}
                                    onClick={toggle}
                                    children={"グループ選択"}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="w-2/5">
                            <div className="flex mt-5 mr-10">
                              <div className="mt-2 flex justify-end min-w-full">
                                <div>
                                  <Button
                                    className={"h-10 text-sm items-center"}
                                    onClick={onClickSearch}
                                    children={"検索　　　　"}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          </div>
                        </tr>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
          </table>
        </div>
      </div>
    </div>
  );
}